"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useReducedMotion } from "@/lib/motion";
import {
  collectFingerprint,
  InsecureContextError,
  type ConsistencyCheck,
  type FingerprintReport,
  type FpSignal,
  type VectorState,
} from "@suite/lib/fingerprint";
import { Card } from "@suite/components/ui/card";
import {
  AlertCircle,
  ArrowRight,
  AudioLines,
  BatteryCharging,
  Box,
  Check,
  CheckCircle2,
  Clock,
  Cpu,
  Database,
  Download,
  Fingerprint,
  Globe,
  Info,
  ListChecks,
  Mic,
  Monitor,
  Palette,
  Play,
  RefreshCw,
  Search,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Type,
  Zap,
  type LucideIcon,
} from "@suite/components/icons";
import { cn } from "@suite/lib/utils";
import CopyButton from "./CopyButton";
import FingerprintGlyph from "./FingerprintGlyph";
import FingerprintScanning from "./FingerprintScanning";

const HEADER = "font-mono text-[9px] font-semibold uppercase tracking-widest text-white/70";

/** In-page jump control in the instrument's own voice: brand-green mono text between
 * terminal-style [ ] brackets (the same bracket language as the glyph's corner marks).
 * Colour makes it unmistakably a control; no pill or fill, which clashed with the vector
 * chips. The brackets are pseudo-elements so every call site stays plain text, and
 * inline-flex (unlike inline) honours vertical margins. Vertical padding cancelled by a
 * negative margin lifts the touch target to ~24px (WCAG 2.5.8) without moving the layout. */
const JUMP_LINK =
  "focus-visible:ring-brand-green/70 text-brand-green before:text-brand-green/40 after:text-brand-green/40 hover:before:text-brand-green hover:after:text-brand-green -my-1.5 inline-flex w-fit items-center gap-1.5 rounded-sm py-1.5 font-mono text-[10px] font-semibold tracking-widest uppercase transition-colors outline-none before:content-['['] after:content-[']'] hover:text-white focus-visible:ring-2";

/** Bubble chrome for the tiny status labels.
 * Wraps rather than running on one line, and capped narrow enough that a bubble anchored to a
 * 6px status dot still lands inside the card at 320px and in RTL. Long labels ("Cross-context
 * values: passed") used to overhang the card by up to 144px and get clipped by its overflow. */
const TIP_LABEL =
  "block max-w-[11rem] rounded bg-black px-1.5 py-0.5 font-mono text-[9px] font-semibold tracking-widest text-white/80 uppercase ring-1 ring-white/25";

/** Bubble chrome for the explanations: one plain wrapping sentence, not a label. */
const TIP_SENTENCE =
  "rounded-md bg-black px-2.5 py-2 font-sans text-[11px] leading-relaxed font-normal tracking-normal text-white/80 normal-case ring-1 ring-white/25";

/** The readout block at the bottom of each verdict column. No rule and no panel of its own:
 * the columns share subgrid rows, so every readout starts on the same track however the text
 * above it wraps, and the jump in scale from body text to mono numerals does the separating.
 *
 * All the container variants here key off the panel's own width (the results Card is a
 * container), not the viewport. Three-up only engages at @4xl (~896px of card), which keeps
 * every column at ≥280px — below that the gauges stack full-width instead of collapsing into
 * slivers. */
const COLUMN_READOUT = "mt-5";

/** Each column spans the panel's five shared rows: eyebrow, verdict, body, readout, jump
 * control. The controls get their own row so all three [ see all … ] pills sit on one
 * baseline however tall the readout above each one runs. In a browser without subgrid this
 * quietly degrades to per-column flow — misaligned, never broken. */
const PANEL_COLUMN = "flex flex-col p-5 @4xl:grid @4xl:row-span-5 @4xl:grid-rows-subgrid";

/** The shared jump-control row: one uniform gap above every control, and self-start so the
 * grid track never stretches a control's hit area. */
const COLUMN_JUMP = "mt-4 @4xl:self-start";

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  "Browser & platform": Globe,
  "Client Hints": ListChecks,
  "Language & time": Clock,
  "Screen & display": Monitor,
  "Input & CSS": SlidersHorizontal,
  "Hardware & network": Cpu,
  Storage: Database,
  "API support": Zap,
  Permissions: ShieldCheck,
  Canvas: Palette,
  WebGL: Box,
  Audio: AudioLines,
  Fonts: Type,
  Media: Play,
  "Media devices": Mic,
  Power: BatteryCharging,
  Maths: Zap,
  "Content blocking": Shield,
};

/** One plain-English line per category so non-experts can follow the report. */
const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  "Browser & platform": "What your browser says it is",
  "Client Hints": "Structured browser data, Chromium only",
  "Language & time": "Your locale and clock",
  "Screen & display": "Your display as pages see it",
  "Input & CSS": "Input devices and display preferences",
  "Hardware & network": "What the browser reports about this machine",
  Storage: "Which storage APIs work here",
  "API support": "Powerful APIs this browser build ships",
  Permissions: "What you've allowed or denied",
  Canvas: "A hidden test image, drawn and read back",
  WebGL: "Your graphics stack's capability profile",
  Audio: "How your browser renders a silent tone",
  Fonts: "Typefaces detected with a ten-font probe",
  Media: "Voices and codecs your browser offers",
  "Media devices": "Cameras, mics and speakers present",
  Power: "Battery state, Chromium only",
  Maths: "Floating-point quirks of this engine and CPU",
  "Content blocking": "Whether ad-style elements were hidden",
};

const VECTOR_ICONS: Record<VectorState["key"], LucideIcon> = {
  canvas: Palette,
  webgl: Box,
  audio: AudioLines,
  fonts: Type,
  voices: Mic,
};

/** Hover explainers for the exposure chips, in plain language: what the surface is, then how
 * we tested it. Still claiming only what the collector measures — canvas/WebGL/audio are tests
 * we can verify (we know what the result should look like), fonts/voices are lists we can only
 * read. */
const VECTOR_HINTS: Record<VectorState["key"], string> = {
  canvas:
    "We draw a hidden test image and read back the pixels. The result varies slightly from machine to machine, which helps sites tell you apart.",
  webgl:
    "Details about your graphics card and driver, plus test images drawn with it, including one through WebGL 2 where your browser supports it.",
  audio:
    "How your browser processes a silent test sound. Different devices produce slightly different results.",
  fonts:
    "Which of ten common font names your browser shows as available. We can only read this list, so there is no way to test whether the browser changed it.",
  voices:
    "The text-to-speech voices your browser offers. We can only read this list, and it loads late and changes easily, so it never counts toward your snapshot.",
};

/** What a vector's outcome means, in words a non-expert can act on. */
const VECTOR_STATUS_TEXT: Record<string, string> = {
  readable: "read",
  blocked: "blocked",
  error: "failed",
  unsupported: "absent",
  timeout: "timed out",
  /** One of two reads failed: unstable, but not evidence of protection either way. */
  inconclusive: "unstable",
  /** Two successful reads disagreed with the known test data intact: the value changed, cause
   * unknown. Neutral — never presented as a defence. */
  varied: "varied",
  /** Known test data came back changed — the only observation that earns this word. */
  scrambled: "scrambled",
  /** Differed from the previous recompute. Could be protection, could be the environment. */
  changed: "changed",
};

/** "a", "a and b", "a, b and c" — for building sentences from measured lists. */
function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** Group signals by category. Map iteration preserves insertion order, so categories keep
 * the order they were collected in. */
function groupByCategory<T extends FpSignal>(signals: T[]): [string, T[]][] {
  const byCat = new Map<string, T[]>();
  for (const s of signals) {
    const items = byCat.get(s.category);
    if (items) items.push(s);
    else byCat.set(s.category, [s]);
  }
  return [...byCat.entries()];
}

/** A signal as displayed: identical to the collector's, plus one presentation flag for notes
 * that must read as neutral observations rather than evidence. */
type DisplaySignal = FpSignal & {
  /** A note is either a neutral observation or a warning; neither is proof of a defence. */
  noteTone?: "neutral" | "warning" | "defence";
};

/** The exact state the UI is showing, derived from the same variables the panel renders —
 * verdict, defended vectors and scrambling evidence included — so neither export can disagree
 * with the screen. Signals are the displayed ones, notes and all. */
interface PresentedReport extends FingerprintReport {
  signals: DisplaySignal[];
  presentation: {
    /** The rendered headline, e.g. "This browser is scrambling its fingerprint". */
    verdict: string;
    /** Vector keys the panel counts as defended (consistent blocking or changed known test
     * data only; never mere instability, and never exposure-only vectors). */
    defendedVectors: string[];
    /** Measured reasons for the scrambling verdict. */
    scramblingEvidence: string[];
    /** Vector keys whose value differed between recomputes. NOT proof of privacy
     * randomisation — an environmental change produces the same symptom. */
    changedAcrossRuns: string[];
    /** Actual comparisons performed (first scan compares against nothing). */
    runsCompared: number;
  };
}

function reportToText(report: PresentedReport): string {
  const { meta, presentation } = report;
  const lines = [
    `Snapshot hash: ${report.hash}`,
    `Algorithm version: ${meta.algorithmVersion}`,
    `Verdict: ${presentation.verdict}.`,
    `Scope: this browser, this session. Not a stable visitor ID.`,
    `Properties displayed: ${meta.signalCount} · included in this hash: ${meta.hashedCount}`,
    `Strong vectors: ${meta.vectorsRead} read, ${meta.vectorsBlocked} blocked, ` +
      `${meta.vectorsScrambled} scrambled, ${meta.vectorsUnstable} unstable, ` +
      `${meta.vectorsUnavailable} unavailable ` +
      `(of ${meta.vectorsTotal})`,
    `Observed as defended: ${
      presentation.defendedVectors.length > 0 ? presentation.defendedVectors.join(", ") : "none"
    } (${presentation.defendedVectors.length} of ${meta.vectorsTotal})`,
    `Changed between recomputes: ${
      presentation.changedAcrossRuns.length > 0
        ? presentation.changedAcrossRuns.join(", ")
        : "none observed"
    } (comparisons: ${presentation.runsCompared})`,
    `Note: stable or session-keyed substitutions can return the same value on every read. ` +
      `A matching repeat therefore means repeatable, not necessarily original: a stable ` +
      `substitution may remain undetected. Reads that merely disagree are reported as ` +
      `varied/unstable (the value changed, cause unknown), never as a defence; "scrambled" ` +
      `is claimed only when known test data came back changed.`,
    `Vector kinds: Draws, Graphics and Sound are controlled integrity tests (known test data, ` +
      `read twice through fresh contexts). Fonts and Voices are exposure-only observations, ` +
      `also read twice, with no known value to check against.`,
    `Exposure-only note: font or voice reads that disagree are instability, not proof of ` +
      `scrambling; and a matching font or voice list can itself be filtered or standardized ` +
      `by the browser while remaining stable. Browser identity never influences any verdict here.`,
    `Collection time: ${(meta.durationMs / 1000).toFixed(1)}s`,
    "",
  ];
  if (presentation.scramblingEvidence.length > 0) {
    lines.push("Scrambling evidence:");
    for (const evidence of presentation.scramblingEvidence) lines.push(`  ${evidence}`);
    lines.push("");
  }
  for (const s of report.signals) {
    const notes = [
      s.status !== "readable" ? s.status : null,
      s.hashed ? null : "not hashed",
      s.note ?? null,
    ].filter(Boolean);
    lines.push(`${s.label}: ${s.value}${notes.length ? ` (${notes.join(", ")})` : ""}`);
  }
  if (report.consistency.length > 0) {
    lines.push("", "Consistency checks:");
    for (const c of report.consistency) lines.push(`[${c.status}] ${c.label}: ${c.detail}`);
  }
  return lines.join("\n");
}

// --- Hash display -------------------------------------------------------------------------

const HEX = "0123456789abcdef";

interface HashDisplayProps {
  hash: string;
  /** Bump to replay the decode animation (e.g. on every recompute). */
  runId: number;
  animate: boolean;
}

/** The hash in 8-char groups with a brief scramble-decode reveal on each run. */
function HashDisplay({ hash, runId, animate }: HashDisplayProps) {
  const [display, setDisplay] = useState(hash);

  useEffect(() => {
    if (!animate) {
      setDisplay(hash);
      return;
    }
    const frames = 22;
    let frame = 0;
    const id = setInterval(() => {
      frame += 1;
      const revealed = Math.floor((frame / frames) * hash.length);
      let out = hash.slice(0, revealed);
      for (let i = revealed; i < hash.length; i++) {
        out += HEX[Math.floor(Math.random() * HEX.length)];
      }
      setDisplay(out);
      if (frame >= frames) clearInterval(id);
    }, 34);
    return () => clearInterval(id);
  }, [hash, runId, animate]);

  const groups: { offset: number; text: string }[] = [];
  for (let i = 0; i < display.length; i += 8) {
    groups.push({ offset: i, text: display.slice(i, i + 8) });
  }

  return (
    <code
      className="text-brand-green font-mono text-sm leading-relaxed sm:text-[15px]"
      aria-label={hash}
    >
      {/* Whole groups wrap as units — break-all used to split a group mid-line. */}
      {groups.map((g) => (
        <span key={g.offset} className="mr-[0.6ch] inline-block last:mr-0">
          {g.text}
        </span>
      ))}
    </code>
  );
}

// --- Small parts ---------------------------------------------------------------------------

interface HoverTipProps {
  /** The explanation. Shown in the bubble and exposed to screen readers as sr-only text. */
  tip: string;
  /** Position + size of the bubble, relative to the nearest positioned ancestor. */
  bubbleClassName: string;
  /** Visual chrome of the bubble itself (TIP_LABEL or TIP_SENTENCE). */
  tipStyle: string;
  as?: "li" | "div";
  className?: string;
  children?: React.ReactNode;
}

/** Hover/focus tooltip that satisfies WCAG 1.4.13, which the old CSS-only tips failed: the
 * trigger is keyboard-focusable (so the bubble shows on Tab and on tap, not just mouse
 * hover), Escape dismisses it without moving focus, and the bubble uses bottom padding
 * instead of a margin gap so the pointer can travel onto it without it closing. The bubble
 * stays aria-hidden — screen readers get the same words from the sr-only twin — and shows on
 * plain group-focus, so a touch user's tap reveals it while the visible focus ring stays
 * keyboard-only (focus-visible). */
function HoverTip({
  tip,
  bubbleClassName,
  tipStyle,
  as: Tag = "div",
  className,
  children,
}: HoverTipProps) {
  const [dismissed, setDismissed] = useState(false);
  return (
    <Tag
      tabIndex={0}
      className={cn(
        "group/tip focus-visible:ring-brand-green/70 rounded-sm outline-none focus-visible:ring-2",
        className
      )}
      onKeyDown={(e) => {
        if (e.key === "Escape") setDismissed(true);
      }}
      onMouseLeave={() => setDismissed(false)}
      onBlur={() => setDismissed(false)}
    >
      {children}
      <span className="sr-only">{tip}</span>
      <span
        aria-hidden
        className={cn(
          "absolute bottom-full z-10 pb-1.5",
          dismissed ? "hidden" : "hidden group-hover/tip:block group-focus/tip:block",
          bubbleClassName
        )}
      >
        <span className={cn("block", tipStyle)}>{tip}</span>
      </span>
    </Tag>
  );
}

function CornerBrackets() {
  return (
    <>
      <span
        className="border-brand-green/50 absolute top-0 left-0 size-2.5 border-t border-l"
        aria-hidden
      />
      <span
        className="border-brand-green/50 absolute top-0 right-0 size-2.5 border-t border-r"
        aria-hidden
      />
      <span
        className="border-brand-green/50 absolute bottom-0 left-0 size-2.5 border-b border-l"
        aria-hidden
      />
      <span
        className="border-brand-green/50 absolute right-0 bottom-0 size-2.5 border-r border-b"
        aria-hidden
      />
    </>
  );
}

type VerdictTone = "good" | "warn" | "bad";

/** The verdict words take the verdict colour — emphasis by meaning, no extra geometry. */
const TONE_HEADLINE: Record<VerdictTone, string> = {
  good: "text-brand-green",
  warn: "text-amber-300",
  bad: "text-red-300",
};

const VERDICT_EYEBROW =
  "font-mono text-[10px] font-semibold uppercase tracking-widest text-white/70";

interface HashDotProps {
  hashed: boolean;
}

/** ● = part of the hashed input, ○ = shown for transparency but not hashed. Deliberately
 * neutral: green elsewhere on this page means "protected", and hash inclusion is not that. */
function HashDot({ hashed }: HashDotProps) {
  return (
    <span
      className={cn(
        "size-1.5 shrink-0 rounded-full",
        hashed ? "bg-white/60" : "ring-1 ring-white/30 ring-inset"
      )}
      aria-hidden
    />
  );
}

interface ConsistencyTileProps {
  check: ConsistencyCheck;
}

function ConsistencyTile({ check }: ConsistencyTileProps) {
  const skipped = check.status === "skipped";
  const Icon = check.status === "ok" ? CheckCircle2 : skipped ? Info : AlertCircle;
  const tone =
    check.status === "ok"
      ? "text-brand-green"
      : check.status === "warn"
        ? "text-amber-300"
        : skipped
          ? "text-white/55"
          : "text-red-300";
  return (
    <div
      className={cn(
        "rounded-lg px-3.5 py-3 ring-1",
        skipped ? "bg-white/[0.01] ring-white/[0.04]" : "bg-white/[0.02] ring-white/[0.06]"
      )}
    >
      <div className="flex items-center gap-2">
        <Icon className={cn("size-4 shrink-0", tone)} aria-hidden />
        <span className={cn("text-sm font-medium", skipped ? "text-white/60" : "text-white/90")}>
          {check.label}
        </span>
        <span
          className={cn(
            "ml-auto shrink-0 font-mono text-[9px] font-semibold tracking-widest uppercase",
            tone
          )}
        >
          {check.status === "ok"
            ? "pass"
            : check.status === "warn"
              ? "warn"
              : skipped
                ? "skipped"
                : "fail"}
        </span>
      </div>
      <p className="mt-1.5 text-[13px] leading-relaxed text-white/60">{check.detail}</p>
    </div>
  );
}

interface SignalRowProps {
  signal: DisplaySignal;
}

function SignalRow({ signal }: SignalRowProps) {
  const unavailable = signal.status !== "readable";
  return (
    <div className="-mx-1.5 flex items-start justify-between gap-3 rounded border-b border-white/[0.04] px-1.5 py-2 transition-colors last:border-0 hover:bg-white/[0.025]">
      <span className="flex shrink-0 items-center gap-2 pt-0.5">
        <HashDot hashed={signal.hashed} />
        <span className="font-mono text-[10px] font-semibold tracking-widest text-white/70 uppercase">
          {signal.label}
        </span>
        <span className="sr-only">
          {signal.hashed ? "included in the hash" : "not included in the hash"}
        </span>
      </span>
      <span className="flex min-w-0 flex-col items-end">
        <span
          className={cn(
            "font-mono text-[13px] [overflow-wrap:anywhere] break-words",
            unavailable ? "text-amber-200/70" : "text-white/85"
          )}
        >
          {signal.value}
        </span>
        {/* Scrambling clues are warnings, not green proof of a privacy defence. */}
        {signal.note && (
          <span
            className={cn(
              "mt-0.5 text-end text-[11px]",
              signal.noteTone === "defence"
                ? "text-brand-green"
                : signal.noteTone === "warning"
                  ? "text-amber-300"
                  : "text-white/60"
            )}
          >
            {signal.note}
          </span>
        )}
      </span>
    </div>
  );
}

interface CategoryCardProps {
  category: string;
  items: DisplaySignal[];
  /** Overrides the stock description when the browser changes what a category means. */
  description?: string;
}

function CategoryCard({ category, items, description: descriptionOverride }: CategoryCardProps) {
  const Icon = CATEGORY_ICONS[category] ?? Fingerprint;
  const hashedCount = items.filter((s) => s.hashed).length;
  const description = descriptionOverride ?? CATEGORY_DESCRIPTIONS[category];
  return (
    <div className="mb-3 break-inside-avoid rounded-lg bg-white/[0.02] ring-1 ring-white/[0.07]">
      <div className="flex items-center gap-2.5 border-b border-white/[0.06] px-3.5 py-2.5">
        <span className="bg-brand-green/10 text-brand-green flex size-6 shrink-0 items-center justify-center rounded-md">
          <Icon className="size-3.5" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-mono text-[10px] font-semibold tracking-widest text-white/80 uppercase">
            {category}
          </span>
          {description && (
            <span className="mt-0.5 block truncate text-[11px] text-white/55">{description}</span>
          )}
        </span>
        <span className="shrink-0 font-mono text-[10px] text-white/55">
          {hashedCount}/{items.length} hashed
        </span>
      </div>
      <div className="px-3.5 pb-1">
        {items.map((s) => (
          <SignalRow key={s.label} signal={s} />
        ))}
      </div>
    </div>
  );
}

// --- Main component --------------------------------------------------------------------------

export default function BrowserFingerprint() {
  const [report, setReport] = useState<FingerprintReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [match, setMatch] = useState<"stable" | "changed" | null>(null);
  const [previousHash, setPreviousHash] = useState<string | null>(null);
  const [runId, setRunId] = useState(0);
  /** Actual run-to-run comparisons performed — the first scan compares against nothing. */
  const [comparisons, setComparisons] = useState(0);
  const [failed, setFailed] = useState<"insecure" | "failed" | null>(null);
  const [query, setQuery] = useState("");
  /** Vectors whose value or measured outcome changed on the last comparable recompute. */
  const [changedAcrossRuns, setChangedAcrossRuns] = useState<Set<string>>(new Set());
  const prevHash = useRef<string | null>(null);
  const prevVersion = useRef<string | null>(null);
  const prevVectors = useRef<Map<string, { outcome: VectorState["outcome"]; raw: string }> | null>(
    null
  );
  const running = useRef(false);
  const requestId = useRef(0);
  const mounted = useRef(true);
  const autoStarted = useRef(false);
  const reduced = useReducedMotion() ?? false;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(async (compare: boolean) => {
    // State updates are asynchronous, so `busy` alone cannot prevent a double click or React
    // Strict Mode's development effect replay from launching overlapping collectors.
    if (running.current) return;
    running.current = true;
    const thisRequest = ++requestId.current;
    if (mounted.current) setBusy(true);
    try {
      // Every asynchronous collector owns a bounded timeout and its cleanup. A second outer
      // Promise.race cannot cancel the losing collection; it would allow a retry to overlap an
      // orphaned scan, so the UI awaits the bounded collector directly.
      const r = await collectFingerprint();
      if (!mounted.current || thisRequest !== requestId.current) return;
      const old = prevHash.current;
      const comparableVersion = prevVersion.current === r.meta.algorithmVersion;
      if (compare && old !== null && comparableVersion) {
        setMatch(old === r.hash ? "stable" : "changed");
        setPreviousHash(old === r.hash ? null : old);
      } else {
        // A fresh baseline (first scan, or a retry after a failure with no prior run): clear
        // any comparison left over from an earlier success, so a retried scan can never show
        // a new hash next to a stale "identical to your last run" badge.
        setMatch(null);
        setPreviousHash(null);
      }
      // Compare each vector against the previous run: a readable value that changed while the
      // browser stayed the same is randomisation, not instability in our own collection.
      // Recomputed fresh each run (not accumulated), so a genuine one-off change clears itself
      // on the next stable run instead of branding the browser for the whole session.
      const before = prevVectors.current;
      if (compare && before && comparableVersion) {
        setComparisons((n) => n + 1);
        const changed = r.vectors
          .filter((v) => {
            const prior = before.get(v.key);
            return Boolean(prior) && (prior?.outcome !== v.outcome || prior.raw !== v.raw);
          })
          .map((v) => v.key);
        setChangedAcrossRuns(new Set(changed));
      } else {
        setChangedAcrossRuns(new Set());
      }
      prevVectors.current = new Map(
        r.vectors.map((v) => [v.key, { outcome: v.outcome, raw: v.raw }])
      );
      prevHash.current = r.hash;
      prevVersion.current = r.meta.algorithmVersion;
      setReport(r);
      setRunId((id) => id + 1);
      setFailed(null);
    } catch (err) {
      if (!mounted.current || thisRequest !== requestId.current) return;
      // Distinguish "this page can't do crypto" from "something blocked the reads" — the old
      // catch-all blamed privacy software even when the real cause was a plain-HTTP origin.
      setFailed(err instanceof InsecureContextError ? "insecure" : "failed");
    } finally {
      if (thisRequest === requestId.current) {
        running.current = false;
        if (mounted.current) setBusy(false);
      }
    }
  }, []);

  useEffect(() => {
    if (autoStarted.current) return;
    autoStarted.current = true;
    void run(false);
  }, [run]);

  /* Every branch renders the same root with the same first child, so React keeps one live
   * region mounted from first paint and only swaps its text. A region that appears already
   * populated is not announced reliably by screen readers. */
  const shell = (announcement: string, children: React.ReactNode) => (
    <div className="ph-no-capture flex flex-col gap-5">
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
      {children}
    </div>
  );

  if (failed) {
    return shell(
      failed === "insecure"
        ? "This tool needs a secure page. Browser crypto is unavailable over plain HTTP."
        : "Couldn't read this browser's signals.",
      <Card className="px-5">
        {/* No role="alert" here: the shell's live region already announces this text, and two
         * live channels made screen readers read the same failure twice. */}
        <div className="flex flex-wrap items-center gap-2.5 text-sm text-white/75">
          <AlertCircle className="size-4 shrink-0 text-amber-300" aria-hidden />
          {failed === "insecure"
            ? "This tool needs a secure page: the browser only provides the Web Crypto hashing it relies on over HTTPS (or on localhost)."
            : "Couldn't read this browser's signals. An extension or privacy setting may be blocking them."}
          {failed === "failed" && (
            <button
              type="button"
              // Keep comparing against the last good run when there is one, so a recompute
              // that failed and was retried behaves like the recompute the user asked for.
              onClick={() => run(prevHash.current !== null)}
              className="focus-visible:ring-brand-green/70 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-white/70 transition-colors outline-none hover:bg-white/[0.06] hover:text-white focus-visible:ring-2"
            >
              <RefreshCw className="size-3.5" aria-hidden /> Try again
            </button>
          )}
        </div>
      </Card>
    );
  }

  if (!report) {
    // The same chassis the route-level loading.tsx already painted, so mounting this component
    // changes nothing on screen — the scan continues in a frame that is already there.
    return shell("Reading your browser's signals.", <FingerprintScanning />);
  }

  /** Signals as displayed. The tone comes from the collector's typed noteKind, never from the
   * note's wording: only changed known-value evidence ("modification") earns the green defence
   * colour, while neutral observations (differing reads, exclusions) stay neutral. A previous
   * version wrapped every note in "Scrambling detected" here, which turned neutral instability
   * notes into false defence claims on the inspector and in both exports. */
  const displaySignals: DisplaySignal[] = report.signals.map((s) => {
    if (!s.note) return s;
    return { ...s, noteTone: s.noteKind === "modification" ? "defence" : "neutral" };
  });
  const groups = groupByCategory(displaySignals);
  const { meta } = report;

  // Consistency: passed / failed / skipped are tracked separately, so a check that never ran
  // is never reported as a pass.
  const okCount = report.consistency.filter((c) => c.status === "ok").length;
  const badCount = report.consistency.filter((c) => c.status === "bad").length;
  const warnCount = report.consistency.filter((c) => c.status === "warn").length;
  const skippedCount = report.consistency.filter((c) => c.status === "skipped").length;
  const ranCount = report.consistency.length - skippedCount;
  // Named in the column when something failed or flagged: the reader wants to know *which*
  // check tripped before deciding whether to scroll to the detail tiles.
  const failedLabels = report.consistency.filter((c) => c.status === "bad").map((c) => c.label);
  const flaggedLabels = report.consistency.filter((c) => c.status === "warn").map((c) => c.label);
  // Only a failed *comparison* is a self-contradiction. An exposed automation flag is a failure
  // worth reporting, but it doesn't contradict anything, so it drives its own verdict.
  const contradictions = report.consistency.filter(
    (c) => c.status === "bad" && c.comparison
  ).length;
  const automationExposed = report.consistency.some(
    (c) => c.label === "Automation flag" && c.status === "bad"
  );

  // Every claim below is something this scan measured. In-scan disagreement is neutral
  // variation (cause unknown); a value differing between recomputes is only "changed" because
  // an ordinary environmental change produces the same symptom. Browser identity never
  // changes the result.
  /** A changed known-value test proves that part of the returned data was modified. It does not
   * certify what happened to every other pixel or sample. Only the controlled tests (canvas,
   * WebGL, audio) can set this — exposure-only vectors have no known value to check. */
  const hasModificationEvidence = (v: VectorState) => Boolean(v.tamperEvidence);

  // Browser identity never creates or changes this verdict. It comes only from changed known
  // test data — two successful reads that merely disagree are "varied"/unstable, never this.
  const scrambledVectors = report.vectors.filter((v) => v.outcome === "scrambled");
  const modifiedVectors = report.vectors.filter((v) => hasModificationEvidence(v));
  // Scrambled is only reachable with tamper evidence; the filter makes that invariant local,
  // so no fallback can ever describe mere disagreement as scrambling evidence.
  const scramblingEvidence = scrambledVectors
    .filter((v) => v.tamperEvidence)
    .map((v) => `${v.label}: ${v.tamperEvidence}.`);
  const MODIFICATION_SUBJECT: Record<string, string> = {
    canvas: "test image",
    webgl: "test render",
    audio: "test tone",
  };
  const modifiedSubjects = modifiedVectors
    .filter((v) => v.key in MODIFICATION_SUBJECT)
    .map((v) => MODIFICATION_SUBJECT[v.key]);
  const modificationClause =
    modifiedSubjects.length > 0 ? `The ${joinList(modifiedSubjects)} came back changed` : null;
  const modificationSummary = modificationClause ? `${modificationClause}.` : "";
  const modified = modifiedVectors.length > 0;
  const scrambling = scrambledVectors.length > 0;
  const inconclusiveVectors = report.vectors.filter((v) => v.outcome === "unstable");
  const inconclusive = inconclusiveVectors.length > 0;
  // Consistent blocking only — a vector that answered once and failed once is flaky, and must
  // not be presented to the user as a privacy defence. Exposure-only vectors (fonts) never
  // count either: they have no ground-truth test, so a read that throws is a failed
  // measurement, not protection.
  const blocking = report.vectors.some((v) => v.outcome === "blocked" && !v.exposureOnly);
  // Deliberately not a uniqueness claim: with no population dataset the tool can only report
  // what it read and whether that repeated. The fallback describes the measurement itself, so
  // it stays true on browsers where some APIs were simply absent rather than defended.
  const headlineParts =
    contradictions > 0
      ? { pre: "This browser ", em: "contradicts itself", tone: "bad" as VerdictTone }
      : automationExposed
        ? {
            pre: "This browser is ",
            em: "announcing that it's automated",
            tone: "bad" as VerdictTone,
          }
        : scrambling
          ? {
              pre: "This browser is ",
              em: "scrambling its fingerprint",
              tone: "good" as VerdictTone,
            }
          : blocking
            ? {
                pre: "This browser is ",
                em: "blocking fingerprint reads",
                tone: "good" as VerdictTone,
              }
            : inconclusive
              ? {
                  pre: "Some fingerprint reads were ",
                  em: "inconclusive",
                  tone: "warn" as VerdictTone,
                }
              : match === "changed"
                ? {
                    pre: "This browser snapshot ",
                    em: "changed",
                    tone: "warn" as VerdictTone,
                  }
                : // Plain-English clean case, claiming only the observed negative: nothing
                  // refused or failed a test this scan ran. No population uniqueness or
                  // browser-protection claim follows, and the repeatability nuance (matching
                  // reads are not proof of originality) stays in the chip tooltips and report.
                  {
                    pre: "Nothing we scanned was ",
                    em: "blocked",
                    tone: "warn" as VerdictTone,
                  };

  /** The headline names a behaviour; this says what that word means before anything else,
   * because every verdict term is jargon to a first-time visitor. Empty on the clean branch,
   * whose headline needs no translation. Each gloss claims only what the scan measured. */
  const verdictGloss =
    contradictions > 0
      ? "Two sources that must agree gave different answers, the first thing anti-bot systems check. "
      : automationExposed
        ? "Its automation flag is set and visible to every site, no fingerprinting needed. "
        : scrambling
          ? `${modificationSummary} `
          : blocking
            ? "Blocking means some reads were refused outright instead of being answered. "
            : inconclusive
              ? "At least one repeated read failed, timed out or returned differing values, so it was not treated as protection and was left out of the snapshot. "
              : match === "changed"
                ? "At least one successful hash input or the usable input set differed from the previous run. That does not prove why it changed. "
                : "";
  // Keep the visible explanation short. The chip tooltips carry the important caveat that
  // repeatability does not prove the value is original.
  const snapshotSentence = scrambledVectors.some((v) => v.randomizedInScan)
    ? "The snapshot leaves out repeat-tested values that did not match. "
    : modified
      ? "Repeat-tested values stay in the snapshot when their two reads match. "
      : verdictGloss
        ? `We read ${meta.signalCount} properties and hashed the ${meta.hashedCount} most repeatable into the snapshot. `
        : `We read ${meta.signalCount} properties ${meta.durationMs < 1000 ? "in under a second" : `in ${(meta.durationMs / 1000).toFixed(1)} seconds`} without asking permission, and hashed the ${meta.hashedCount} most repeatable into the snapshot. `;

  // One chip per strong vector, labelled by what it is to a person rather than by its API,
  // and carrying the vector's real outcome so the state is never colour-only.
  const identifierChips = report.vectors.map((v) => {
    const scrambled = v.outcome === "scrambled";
    const modificationDetected = hasModificationEvidence(v);
    const changed = changedAcrossRuns.has(v.key) && v.outcome !== "scrambled";
    // Same epistemics as before, in plain words: fonts/voices disagreement is flakiness (never
    // proof of scrambling), and a matching read is only what sites can see.
    const stabilityHint = v.exposureOnly
      ? v.randomizedInScan
        ? "The list changed between our two reads, so we left it out of your snapshot. That's flakiness, not proof of a privacy tool."
        : v.outcome === "read"
          ? "We read it twice and got the same list. That's what sites see, which may not be everything on your system."
          : v.outcome === "unstable"
            ? "We couldn't get a reliable reading, so no conclusion either way."
            : ""
      : v.randomizedInScan
        ? "We ran the test twice and got different answers, so we left it out of your snapshot. A glitch and a privacy tool look the same here."
        : v.outcome === "unstable"
          ? "The test didn't complete reliably, so we left it out of your snapshot."
          : modificationDetected
            ? "Our test came back altered. Something is changing what sites see here."
            : v.repeatableInScan
              ? "We ran the test twice and got the same answer, so it's in your snapshot. A privacy tool that always returns the same fake value would look identical."
              : "";
    return {
      key: v.key,
      label: v.label,
      icon: VECTOR_ICONS[v.key],
      hint: `${VECTOR_HINTS[v.key]}${stabilityHint ? ` ${stabilityHint}` : ""}`,
      on: v.outcome === "read" || v.outcome === "scrambled",
      inconclusive: v.outcome === "unstable",
      changed,
      /** Only consistent blocking and changed known test data count here. Instability of any
       * kind, an absent API, a flaky read or a cross-recompute change does not. */
      defended: (v.outcome === "blocked" && !v.exposureOnly) || scrambled,
      randomized: v.randomizedInScan,
      modificationDetected,
      statusText: scrambled
        ? VECTOR_STATUS_TEXT.scrambled
        : v.outcome === "unstable"
          ? // The most precise word for what actually happened: "varied" when two successful
            // reads disagreed, "timed out" for a loading race, "unstable" for the rest — none
            // of which is a stability judgement or a defence.
            v.randomizedInScan
            ? VECTOR_STATUS_TEXT.varied
            : v.status === "timeout"
              ? VECTOR_STATUS_TEXT.timeout
              : VECTOR_STATUS_TEXT.inconclusive
          : changed
            ? v.outcome === "read"
              ? VECTOR_STATUS_TEXT.changed
              : `${v.outcome} · ${VECTOR_STATUS_TEXT.changed}`
            : v.outcome === "read"
              ? VECTOR_STATUS_TEXT.readable
              : v.outcome === "unavailable"
                ? VECTOR_STATUS_TEXT.unsupported
                : VECTOR_STATUS_TEXT.blocked,
    };
  });
  // Vector-level, so one dead WebGL context counts once instead of filling the gauge.
  // Derived from the same predicate the chips use, so the total can never disagree with the
  // dots beside it (vectorsBlocked counts only the first read and double-counted flaky ones).
  const defended = identifierChips.filter((chip) => chip.defended).length;
  // Green in the Exposure column is earned only when every unreadable vector is explained by
  // an observed defence — a timeout or an absent API is not protection, so "4 of 5 readable"
  // used to wear green while the Defences column said nothing was defended.
  const unreadableUndefended = identifierChips.some((chip) => !chip.on && !chip.defended);

  /** Built from the very variables the panel renders — verdict, defended set, hints and
   * displayed notes — so the copied report and the JSON download cannot disagree with the
   * screen by construction. */
  const presented: PresentedReport = {
    ...report,
    signals: displaySignals,
    presentation: {
      verdict: `${headlineParts.pre}${headlineParts.em}`,
      defendedVectors: identifierChips.filter((chip) => chip.defended).map((chip) => chip.key),
      scramblingEvidence,
      changedAcrossRuns: [...changedAcrossRuns],
      runsCompared: comparisons,
    },
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(presented, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "browser-fingerprint.json";
    a.hidden = true;
    document.body.append(a);
    a.click();
    a.remove();
    // Safari may not consume the object URL until after the click task has returned.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const q = query.trim().toLowerCase();
  const filteredGroups = q
    ? groups
        .map(
          ([category, items]) =>
            [
              category,
              items.filter((s) => `${category} ${s.label} ${s.value}`.toLowerCase().includes(q)),
            ] as [string, FpSignal[]]
        )
        .filter(([, items]) => items.length > 0)
    : groups;
  const matchCount = filteredGroups.reduce((n, [, items]) => n + items.length, 0);

  /* ph-no-capture on the shell keeps the hash and raw signal values out of PostHog session
   * replay, so "your fingerprint never leaves this page" holds even for consenting visitors. */
  return shell(
    busy
      ? "Reading your browser's signals."
      : `Scan complete. ${headlineParts.pre}${headlineParts.em}. ${meta.vectorsReadable} of ${meta.vectorsTotal} vectors readable, ${defended} observed as defended, ${okCount} of ${ranCount} consistency checks passed.`,
    /* No entrance animation here on purpose. Fading the report up from transparent leaves one
     * frame where the chassis has unmounted and the values haven't painted yet, and that empty
     * frame is what reads as a flash. The skeleton already drew this exact geometry, so the
     * values simply appear in slots that are already on screen — nothing moves, nothing dims.
     * The hash's own scramble-in (HashDisplay) carries the sense of arrival. */
    <div className="flex flex-col gap-5">
      {/* One split ledger: a single vertical hairline runs the card's full height, cutting a
       * recessed snapshot quadrant out of the top right. The same line continues down as the
       * gauge panel's last divider, so the quadrant IS the rightmost column — identical
       * width by construction, no floating box to misalign. The quadrant carries the card's
       * whole provenance (label, run readout, controls), which retires the header strip and
       * leaves exactly one horizontal hairline in the card. */}
      <Card className="@container relative gap-0 overflow-hidden p-0">
        {/* Clipped in its own wrapper: an element hanging past the card's right edge makes the
         * card a scrollable container, and any focus near that edge scrolls the content 64px
         * left with no way back (overflow-hidden still scrolls programmatically). */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <div className="bg-brand-green/[0.06] absolute -top-24 -right-16 size-72 rounded-full blur-3xl" />
        </div>

        <div className="relative flex flex-col @4xl:grid @4xl:grid-cols-3">
          <div className="flex min-w-0 flex-col justify-center gap-2 p-5 @4xl:col-span-2 @4xl:max-w-3xl @4xl:py-7 @4xl:pr-10">
            <h2 className="text-2xl font-semibold tracking-tight text-white sm:text-[26px]">
              {headlineParts.pre}
              <span className={TONE_HEADLINE[headlineParts.tone]}>{headlineParts.em}</span>.
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-white/65">
              {verdictGloss}
              {snapshotSentence}
              {match === "stable"
                ? "It repeated exactly on your last recompute."
                : match === "changed"
                  ? "The snapshot changed on your last recompute. A value changed, or the set of values we could use was different."
                  : "Hit Recompute to see whether it repeats."}
            </p>
          </div>

          {/* The snapshot quadrant: recessed black glass behind the card's one full-height
           * vertical. It owns the fingerprint label, the run readout and both controls, so
           * everything machine-written lives on one surface. */}
          <div className="relative flex flex-col gap-3 border-t border-white/[0.06] bg-black/25 p-5 @4xl:border-t-0 @4xl:border-l">
            <div className="flex items-center justify-between gap-2">
              <span className={HEADER}>Your browser snapshot</span>
              <div className="flex items-center gap-0.5">
                <CopyButton value={report.hash} label="snapshot hash" />
                <button
                  type="button"
                  onClick={() => run(true)}
                  disabled={busy}
                  className="focus-visible:ring-brand-green/70 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-white/70 transition-colors outline-none hover:bg-white/[0.06] hover:text-white focus-visible:ring-2 disabled:opacity-50"
                >
                  <RefreshCw
                    className={cn("size-3.5", busy && !reduced && "animate-spin")}
                    aria-hidden
                  />
                  Recompute
                </button>
              </div>
            </div>
            {/* min-w on the hash makes it wrap under the glyph in narrow columns instead of
             * squeezing into one hex group per line. */}
            <div className="flex flex-wrap items-center gap-2.5">
              <HoverTip
                tip="Visual checksum of the hash: the same hash always draws the same pattern."
                bubbleClassName="start-0 w-56"
                tipStyle={TIP_SENTENCE}
                className="relative shrink-0 p-2.5"
              >
                <CornerBrackets />
                <FingerprintGlyph
                  hash={report.hash}
                  className={cn(
                    "size-14 transition-opacity motion-reduce:transition-none",
                    busy && "opacity-40"
                  )}
                />
              </HoverTip>
              <div className="min-w-44 flex-1">
                <HashDisplay hash={report.hash} runId={runId} animate={!reduced} />
              </div>
            </div>
            <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="font-mono text-[9px] font-semibold tracking-widest text-white/45 uppercase">
                sha-256 · run {String(runId).padStart(2, "0")} ·{" "}
                {(meta.durationMs / 1000).toFixed(1)}s · computed locally
              </span>
              {match === "stable" && (
                <span className="bg-brand-green/15 text-brand-green inline-flex w-fit items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium">
                  <Check className="size-3" aria-hidden /> Identical to your last run
                </span>
              )}
              {match === "changed" && (
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-amber-400/15 px-2 py-0.5 text-[11px] font-medium text-amber-300">
                  Changed since your last run
                  {previousHash && (
                    <span className="inline-flex items-center gap-1" aria-hidden>
                      <FingerprintGlyph hash={previousHash} className="size-4 opacity-50" />
                      <ArrowRight className="size-2.5" />
                      <FingerprintGlyph hash={report.hash} className="size-4" />
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* One instrument panel, three readouts. The columns share four subgrid rows, so
         * eyebrows, verdicts, bodies and readouts each sit on one line across the panel — a
         * two-line body in one column can never push its numeral out of register with its
         * neighbours'. This border-t is the card's only horizontal hairline; its verticals
         * are the column dividers, the last of which continues the quadrant's edge above. */}
        <div className="relative grid divide-y divide-white/[0.06] border-t border-white/[0.06] @4xl:grid-cols-3 @4xl:grid-rows-[auto_auto_auto_1fr_auto] @4xl:divide-x @4xl:divide-y-0">
          {/* Exposure: one chip per strong vector, each stating its own outcome in words. */}
          <div className={PANEL_COLUMN}>
            <span className={VERDICT_EYEBROW}>Exposure</span>
            {/* Green only when the unreadable vectors were actually defended; an unreadable
             * vector that merely timed out or doesn't exist here renders neutral. */}
            <p
              className={cn(
                "mt-1.5 text-[17px] leading-snug font-semibold",
                meta.vectorsReadable === meta.vectorsTotal
                  ? "text-amber-300"
                  : unreadableUndefended
                    ? "text-white/85"
                    : "text-brand-green"
              )}
            >
              {meta.vectorsReadable === meta.vectorsTotal
                ? "Every signature was readable."
                : `${meta.vectorsReadable} of ${meta.vectorsTotal} signatures readable.`}
            </p>
            {/* Not "five surfaces we test for tampering": only canvas, WebGL and audio get
             * known-value readbacks; fonts and voices are exposure-only observations — that
             * distinction is carried by each chip's tooltip/sr-only text and the exports. */}
            <p className="mt-1 text-[13px] leading-relaxed text-white/65">
              Five high-signal surfaces trackers combine.
            </p>
            <div className={COLUMN_READOUT}>
              {/* One row for all five chips. The controlled-vs-observed distinction is carried
               * by each chip's tooltip (which is also sr-only text) and by both exports, so it
               * stays accessible without a second row of group labels. The ul is the
               * positioning context: each chip's explainer spans the full list width above it
               * instead of hanging off one chip, so it can't be clipped by the card's overflow
               * boundary at narrow widths (or in RTL). */}
              <ul className="relative flex flex-wrap gap-1.5">
                {identifierChips.map((chip) => (
                  <HoverTip
                    key={chip.key}
                    as="li"
                    tip={chip.hint}
                    bubbleClassName="inset-x-0"
                    tipStyle={TIP_SENTENCE}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 ring-1",
                      chip.on && !chip.randomized
                        ? "bg-white/[0.04] ring-white/[0.09]"
                        : "bg-white/[0.015] ring-white/[0.04]"
                    )}
                  >
                    <chip.icon
                      className={cn("size-3.5", chip.on ? "text-white/75" : "text-white/45")}
                      aria-hidden
                    />
                    <span
                      className={cn(
                        "font-mono text-[9px] font-semibold tracking-widest uppercase",
                        chip.on ? "text-white/70" : "text-white/55"
                      )}
                    >
                      {chip.label}
                    </span>
                    {/* Status in text, not colour alone, and available to screen readers.
                     * Minimum white/55 so 9px type still clears WCAG AA contrast. Green only
                     * when the vector is actually counted as defended — an absent API, a
                     * timeout or a blocked exposure-only read must not wear that colour. */}
                    <span
                      className={cn(
                        "font-mono text-[9px] tracking-wider",
                        chip.defended ? "text-brand-green" : "text-white/55"
                      )}
                    >
                      {chip.statusText}
                    </span>
                  </HoverTip>
                ))}
              </ul>
            </div>
            {/* The "your real exposure" idea, kept as a pointer instead of a paragraph. */}
            <a
              href="#signal-inspector"
              className={cn(JUMP_LINK, COLUMN_JUMP)}
              aria-label={`Your full exposure is all ${meta.signalCount} properties. Jump to the signal inspector.`}
            >
              see all {meta.signalCount} properties <span aria-hidden>↓</span>
            </a>
          </div>

          {/* Defences: counted per vector, so one dead collector can't inflate the number. */}
          <div className={PANEL_COLUMN}>
            <span className={VERDICT_EYEBROW}>Defences</span>
            <p
              className={cn(
                "mt-1.5 text-[17px] leading-snug font-semibold",
                defended > 0 ? "text-brand-green" : "text-amber-300"
              )}
            >
              {scrambling && blocking
                ? "Blocking and scrambling."
                : scrambling
                  ? "Scrambling, not blocking."
                  : blocking
                    ? "Blocking reads."
                    : "Nothing defended this scan."}
            </p>
            <p className="mt-1 text-[13px] leading-relaxed text-white/65">
              {/* Scrambled always carries modification evidence, so modificationSummary covers
               * it; a "reads disagreed" sentence here would restate the removed old semantics. */}
              {modified
                ? modificationSummary
                : inconclusive
                  ? "Some reads were inconclusive; that is not counted as a defence."
                  : blocking
                    ? "Something refused these reads outright."
                    : meta.vectorsUnavailable > 0
                      ? "Nothing was blocked. Some of these surfaces weren't available to read here."
                      : "No blocked or scrambled signals found."}
            </p>
            <div className={COLUMN_READOUT}>
              {/* items-start + a numeral-height pill row: the pills centre on the big
               * number's line instead of drifting down beside the label. */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-2xl leading-none font-semibold text-white/90">
                    {defended}
                  </div>
                  <p className="mt-1.5 font-mono text-[9px] tracking-widest text-white/60 uppercase">
                    of {meta.vectorsTotal} observed as defended
                  </p>
                </div>
                <ul className="flex h-6 items-center gap-1">
                  {identifierChips.map((chip) => (
                    <HoverTip
                      key={chip.key}
                      as="li"
                      tip={`${chip.label}: ${chip.statusText}`}
                      bubbleClassName="end-0"
                      tipStyle={TIP_LABEL}
                      className="relative inline-flex size-6 items-center justify-center rounded-sm"
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "h-3.5 w-1.5 rounded-sm",
                          chip.defended
                            ? "bg-brand-green shadow-[0_0_6px_rgba(91,228,155,0.5)]"
                            : "bg-white/10"
                        )}
                      />
                    </HoverTip>
                  ))}
                </ul>
              </div>
            </div>
            {/* The precise counts, one disclosure away, on the shared control row. They also
             * sit as notes on the matching inspector rows, so the evidence is never more than a
             * click from the claim. */}
            {scramblingEvidence.length > 0 && (
              <details className={cn("group", COLUMN_JUMP)}>
                <summary
                  className={cn(
                    JUMP_LINK,
                    "cursor-pointer list-none select-none [&::-webkit-details-marker]:hidden"
                  )}
                >
                  <span
                    aria-hidden
                    className="transition-transform group-open:rotate-90 motion-reduce:transition-none"
                  >
                    ▸
                  </span>
                  what we measured
                </summary>
                <ul className="mt-1.5 flex flex-col gap-1">
                  {scramblingEvidence.map((line) => (
                    <li key={line} className="text-[11px] leading-relaxed text-white/55">
                      {line}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          {/* Consistency: big numeral + one LED block per cross-check, lit by its result. */}
          <div className={PANEL_COLUMN}>
            <span className={VERDICT_EYEBROW}>Consistency</span>
            <p
              className={cn(
                "mt-1.5 text-[17px] leading-snug font-semibold",
                badCount > 0
                  ? "text-red-300"
                  : warnCount > 0
                    ? "text-amber-300"
                    : "text-brand-green"
              )}
            >
              {badCount > 0
                ? `${badCount} check${badCount === 1 ? "" : "s"} failed.`
                : warnCount > 0
                  ? `${warnCount} check${warnCount === 1 ? "" : "s"} flagged.`
                  : `Passed all ${ranCount} checks we ran.`}
            </p>
            {/* Names the tripped checks when there are any; otherwise says what the checks are
             * for. Skips are stated plainly either way, so they're never mistaken for passes. */}
            <p className="mt-1 text-[13px] leading-relaxed text-white/65">
              {ranCount === 0
                ? "None of these checks could run in this browser."
                : failedLabels.length > 0 || flaggedLabels.length > 0
                  ? [
                      failedLabels.length > 0 ? `Failed: ${joinList(failedLabels)}.` : null,
                      flaggedLabels.length > 0 ? `Flagged: ${joinList(flaggedLabels)}.` : null,
                    ]
                      .filter(Boolean)
                      .join(" ")
                  : "Checks that spoofed browsers tend to fail."}
              {skippedCount > 0 && ` ${skippedCount} couldn't run in this browser.`}
            </p>
            <div className={COLUMN_READOUT}>
              {/* items-start + a numeral-height LED row, mirroring the Defences readout: the
               * dots centre on the 7/7 line rather than sinking beside the jump link. */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-2xl leading-none font-semibold text-white/90">
                    {okCount}
                    <span className="text-sm text-white/55">/{ranCount}</span>
                  </div>
                  <p className="mt-1.5 font-mono text-[9px] tracking-widest text-white/60 uppercase">
                    checks passed
                  </p>
                </div>
                <ul className="flex h-6 items-center gap-1">
                  {report.consistency.map((c) => {
                    const statusWord =
                      c.status === "ok" ? "passed" : c.status === "skipped" ? "skipped" : c.status;
                    return (
                      <HoverTip
                        key={c.label}
                        as="li"
                        tip={`${c.label}: ${statusWord}`}
                        bubbleClassName="end-0"
                        tipStyle={TIP_LABEL}
                        className="relative inline-flex size-6 items-center justify-center rounded-sm"
                      >
                        <span
                          aria-hidden
                          className={cn(
                            "size-2 rounded-[3px]",
                            c.status === "ok"
                              ? "bg-brand-green shadow-[0_0_6px_rgba(91,228,155,0.5)]"
                              : c.status === "warn"
                                ? "bg-amber-300 shadow-[0_0_6px_rgba(252,211,77,0.5)]"
                                : c.status === "bad"
                                  ? "bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.5)]"
                                  : // Skipped: visibly hollow, never lit as if it passed.
                                    "bg-transparent ring-1 ring-white/25 ring-inset"
                          )}
                        />
                      </HoverTip>
                    );
                  })}
                </ul>
              </div>
            </div>
            {/* The target card only renders when checks exist, so the link is gated the same. */}
            {report.consistency.length > 0 && (
              <a
                href="#consistency-checks"
                className={cn(JUMP_LINK, COLUMN_JUMP)}
                aria-label={`All ${report.consistency.length} checks. Jump to the detailed results.`}
              >
                see all {report.consistency.length} checks <span aria-hidden>↓</span>
              </a>
            )}
          </div>
        </div>
      </Card>

      {report.consistency.length > 0 && (
        <Card
          id="consistency-checks"
          tabIndex={-1}
          className="focus-visible:ring-brand-green/70 scroll-mt-24 px-5 outline-none focus-visible:ring-2"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className={HEADER}>Consistency checks</span>
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold tracking-wider uppercase",
                badCount + warnCount === 0
                  ? "bg-brand-green/10 text-brand-green"
                  : "bg-amber-400/10 text-amber-300"
              )}
            >
              {badCount + warnCount === 0 ? (
                <>
                  <Check className="size-3" aria-hidden /> passed {okCount} of {ranCount}
                </>
              ) : (
                `${okCount} passed · ${badCount + warnCount} flagged`
              )}
              {skippedCount > 0 && ` · ${skippedCount} skipped`}
            </span>
          </div>
          <p className="max-w-3xl text-xs text-white/70">
            Anti-bot systems don&apos;t just read these values, they cross-check them. A mismatch
            between APIs is a stronger tell than any single signal. These are the basic cross-checks
            this tool runs, not the full set a commercial anti-bot service uses.
          </p>
          <div className="mt-1 grid gap-2 sm:grid-cols-2">
            {report.consistency.map((c) => (
              <ConsistencyTile key={c.label} check={c} />
            ))}
          </div>
        </Card>
      )}

      <Card
        id="signal-inspector"
        tabIndex={-1}
        className="focus-visible:ring-brand-green/70 scroll-mt-24 px-5 outline-none focus-visible:ring-2"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={HEADER}>Signal inspector ({report.signals.length})</span>
          <div className="flex items-center gap-1.5">
            <div className="relative">
              <Search
                className="pointer-events-none absolute start-2 top-1/2 size-3 -translate-y-1/2 text-white/55"
                aria-hidden
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter signals…"
                aria-label="Filter signals"
                className="focus:ring-brand-green/40 w-36 rounded-md bg-white/[0.04] py-1 ps-7 pe-2 text-xs text-white/85 ring-1 ring-white/[0.08] outline-none placeholder:text-white/55 focus:ring-1 sm:w-48"
              />
            </div>
            <CopyButton value={reportToText(presented)} label="full report" />
            <button
              type="button"
              onClick={downloadJson}
              aria-label="Download as JSON"
              className="focus-visible:ring-brand-green/70 inline-flex size-6 shrink-0 items-center justify-center rounded-md text-white/70 transition-colors outline-none hover:bg-white/[0.06] hover:text-white focus-visible:ring-2"
            >
              <Download className="size-3.5" aria-hidden />
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-white/55">
          <span className="flex items-center gap-1.5">
            <HashDot hashed /> hashed into the snapshot
          </span>
          {/* "Never part of the hash", not "changes too often": some shown-only rows are
           * excluded for other reasons (evidence-only readbacks, redundant counts). */}
          <span className="flex items-center gap-1.5">
            <HashDot hashed={false} /> shown only, never part of the hash
          </span>
          {q && (
            <span className="text-white/60">
              {matchCount} of {report.signals.length} match
            </span>
          )}
        </div>
        {filteredGroups.length > 0 ? (
          <div className="gap-3 md:columns-2">
            {filteredGroups.map(([category, items]) => (
              <CategoryCard key={category} category={category} items={items} />
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-white/55">
            No signals match &ldquo;{query}&rdquo;
          </p>
        )}
      </Card>
    </div>
  );
}
